/**
 * Хук для получения контекста текущей страницы
 */

import { useLocation, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import type { PageContext } from './types';

// Маппинг ресурсов на русские названия
const RESOURCE_LABELS: Record<string, string> = {
  'dashboard': 'Главная',
  'users': 'Пользователи',
  'admin-users': 'Администраторы',
  'roles': 'Роли',
  'courses': 'Курсы',
  'modules': 'Модули',
  'module-items': 'Элементы модуля',
  'teachers': 'Учителя',
  'articles': 'Статьи',
  'announcements': 'Анонсы',
  'banners': 'Баннеры',
  'subscriptions': 'Владельцы абонементов',
  'subscription-plans': 'Абонементы',
  'orders': 'Заказы',
  'payments': 'Платежи',
  'broadcasts': 'Рассылки',
  'triggers': 'Триггеры',
  'telegram-channels': 'Telegram каналы',
  'bot-tokens': 'Токены бота',
  'ai': 'AI настройки',
  'knowledge-documents': 'База знаний',
  'translations': 'Переводы',
  'languages': 'Языки',
  'integrations': 'Интеграции',
  'support-tickets': 'Тикеты поддержки',
  'daily-stats': 'Ежедневная статистика',
};

// Используется в formatPageContextForAgent
const ACTION_LABELS: Record<string, string> = {
  'list': 'просмотр списка',
  'create': 'создание',
  'edit': 'редактирование',
  'show': 'просмотр',
};

/**
 * Парсит путь и извлекает ресурс, действие и ID
 */
function parsePath(pathname: string): {
  resource: string | null;
  action: 'list' | 'create' | 'edit' | 'show' | null;
  recordId: string | null;
} {
  // Убираем начальный слэш
  const path = pathname.startsWith('/') ? pathname.slice(1) : pathname;
  const segments = path.split('/').filter(Boolean);

  if (segments.length === 0) {
    return { resource: 'dashboard', action: 'list', recordId: null };
  }

  const resource = segments[0];

  // /resource - список
  if (segments.length === 1) {
    return { resource, action: 'list', recordId: null };
  }

  // /resource/create
  if (segments[1] === 'create') {
    return { resource, action: 'create', recordId: null };
  }

  // /resource/edit/:id
  if (segments[1] === 'edit' && segments[2]) {
    return { resource, action: 'edit', recordId: segments[2] };
  }

  // /resource/show/:id
  if (segments[1] === 'show' && segments[2]) {
    return { resource, action: 'show', recordId: segments[2] };
  }

  // /resource/:id - предполагаем show
  if (segments[1] && !['create', 'edit', 'show'].includes(segments[1])) {
    return { resource, action: 'show', recordId: segments[1] };
  }

  return { resource, action: 'list', recordId: null };
}

/**
 * Генерирует человекочитаемое описание страницы
 */
function generateDescription(
  resource: string | null,
  action: 'list' | 'create' | 'edit' | 'show' | null,
  recordId: string | null
): string {
  if (!resource) {
    return 'Главная страница';
  }

  const resourceLabel = RESOURCE_LABELS[resource] || resource;

  if (action === 'list') {
    return `Страница "${resourceLabel}" — просмотр списка`;
  }

  if (action === 'create') {
    return `Страница "${resourceLabel}" — создание новой записи`;
  }

  if (action === 'edit' && recordId) {
    return `Страница "${resourceLabel}" — редактирование записи #${recordId}`;
  }

  if (action === 'show' && recordId) {
    return `Страница "${resourceLabel}" — просмотр записи #${recordId}`;
  }

  return `Страница "${resourceLabel}"`;
}

/**
 * Хук для получения контекста текущей страницы
 */
export function usePageContext(): PageContext {
  const location = useLocation();
  const params = useParams();

  return useMemo(() => {
    const { resource, action, recordId } = parsePath(location.pathname);

    // Если есть :id в params, используем его
    const finalRecordId = params.id || recordId;

    return {
      path: location.pathname,
      resource,
      action,
      recordId: finalRecordId || null,
      description: generateDescription(resource, action, finalRecordId || null),
    };
  }, [location.pathname, params.id]);
}

/**
 * Форматирует контекст страницы для отправки агенту
 */
export function formatPageContextForAgent(context: PageContext): string {
  const parts: string[] = [
    `Текущая страница: ${context.description}`,
    `Путь: ${context.path}`,
  ];

  if (context.resource) {
    const resourceLabel = RESOURCE_LABELS[context.resource] || context.resource;
    parts.push(`Ресурс: ${resourceLabel} (${context.resource})`);
  }

  if (context.action) {
    const actionLabel = ACTION_LABELS[context.action] || context.action;
    parts.push(`Действие: ${actionLabel}`);
  }

  if (context.recordId) {
    parts.push(`ID записи: ${context.recordId}`);
  }

  return parts.join('\n');
}
